import type { ForecastFactorRow } from 'src/api/rpc/hooks/procurement/useForecastFactors';

export interface FactorPeriod {
  startsAt: string;
  /** Exclusive UTC month boundary; null means no end. */
  endsAt: string | null;
  override: ForecastFactorRow | null;
}

export interface FactorUsage {
  periods: FactorPeriod[];
  status: 'throughout' | 'partial' | 'overridden';
}

/**
 * Resolve the saved schedule from this UTC month onward, including windows
 * beyond the forecast horizon. Only window boundaries matter, so an open-ended
 * override never requires enumerating an unbounded number of months.
 * Matches the backend's item_loc precedence: created_at DESC, id DESC.
 */
export function buildFactorSchedule(
  overrides: readonly ForecastFactorRow[],
  now: Date,
): FactorPeriod[] {
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  const rows = overrides
    .filter((o) => o.isActive && o.deactivatedAt === null)
    .toSorted((a, b) => {
      const created = Date.parse(b.createdAt) - Date.parse(a.createdAt);
      return created || (a.id === b.id ? 0 : a.id > b.id ? -1 : 1);
    });
  const boundaries = new Set([start]);
  for (const row of rows) {
    boundaries.add(Math.max(start, Date.parse(row.startsAt)));
    if (row.endsAt !== null && Date.parse(row.endsAt) > start) {
      boundaries.add(Date.parse(row.endsAt));
    }
  }
  const dates = [...boundaries].sort((a, b) => a - b);
  const periods: FactorPeriod[] = [];
  for (const [index, date] of dates.entries()) {
    const override =
      rows.find(
        (o) =>
          Date.parse(o.startsAt) <= date &&
          (o.endsAt === null || Date.parse(o.endsAt) > date),
      ) ?? null;
    const next = dates[index + 1];
    const endsAt = next === undefined ? null : new Date(next).toISOString();
    const previous = periods.at(-1);
    if (previous && previous.override?.id === override?.id) {
      previous.endsAt = endsAt;
    } else {
      periods.push({
        startsAt: new Date(date).toISOString(),
        endsAt,
        override,
      });
    }
  }
  return periods;
}

/** Usage describes the remaining configured window, not historical months. */
export function factorUsage(
  row: ForecastFactorRow,
  schedule: readonly FactorPeriod[],
): FactorUsage {
  const periods = schedule.filter((p) => p.override?.id === row.id);
  if (periods.length === 0) return { periods, status: 'overridden' };
  const hasOtherWinner = schedule.some(
    (p) =>
      p.override?.id !== row.id &&
      (row.endsAt === null ||
        Date.parse(p.startsAt) < Date.parse(row.endsAt)) &&
      (p.endsAt === null || Date.parse(p.endsAt) > Date.parse(row.startsAt)),
  );
  return { periods, status: hasOtherWinner ? 'partial' : 'throughout' };
}
